import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';

import {
  createJianyingCopyPreset,
  deleteJianyingCopyPreset,
  updateJianyingCopyPreset,
} from '../../../services/jianyingTemplateService.js';

const errorMessage = (error, fallback) => {
  const fieldErrors = error.response?.data?.field_errors;
  const firstFieldError = fieldErrors && typeof fieldErrors === 'object'
    ? Object.values(fieldErrors).flat().find((item) => typeof item === 'string')
    : null;
  return firstFieldError || error.response?.data?.message || error.message || fallback;
};

const createEmptyValues = (textSlots) => Object.fromEntries(
  textSlots.map((slot) => [slot.id, '']),
);

const getAvailableName = (requestedName, presets) => {
  const names = new Set(presets.map((preset) => preset.name));
  if (!names.has(requestedName)) return requestedName;
  let suffix = 2;
  while (names.has(`${requestedName} ${suffix}`)) suffix += 1;
  return `${requestedName} ${suffix}`;
};

const COPY_VARIABLE = '{{客户姓名}}';

const CopyValueField = ({ disabled, onChange, slot, value }) => {
  const inputRef = useRef(null);
  const insertVariable = () => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? start;
    const nextValue = `${value.slice(0, start)}${COPY_VARIABLE}${value.slice(end)}`;
    onChange(nextValue);
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + COPY_VARIABLE.length, start + COPY_VARIABLE.length);
    });
  };
  return (
    <Form.Group>
      <div className="jianying-copy-editor__field-label">
        <Form.Label>{slot.name}</Form.Label>
        <small>{slot.slot_settings?.max_length ? `最多 ${slot.slot_settings.max_length} 字` : '不限字数'}</small>
      </div>
      <Form.Control
        ref={inputRef}
        as={slot.slot_settings?.input_mode === 'multiline' ? 'textarea' : 'input'}
        rows={slot.slot_settings?.input_mode === 'multiline' ? 3 : undefined}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
      <div className="jianying-copy-editor__field-help">
        <Form.Text className="jianying-copy-editor__original-content">
          原始内容：{slot.original_content || '空'}
        </Form.Text>
        <button
          type="button"
          className="jianying-copy-editor__token"
          disabled={disabled || value.includes(COPY_VARIABLE)}
          onClick={insertVariable}
        >
          客户姓名
        </button>
      </div>
    </Form.Group>
  );
};

const JianyingCopyPresetEditor = ({ onChanged, template }) => {
  const presets = useMemo(
    () => (Array.isArray(template.copy_presets) ? template.copy_presets : []),
    [template.copy_presets],
  );
  const textSlots = useMemo(() => (
    (Array.isArray(template.slots) ? template.slots : [])
      .filter((slot) => slot.kind === 'text')
  ), [template.slots]);
  const textSlotGroups = useMemo(() => {
    const groups = Array.isArray(template.slot_groups) ? template.slot_groups : [];
    const groupsById = new Map(groups.map((group) => [group.id, group]));
    const getGroupPath = (groupId) => {
      const names = [];
      const visited = new Set();
      let currentId = groupId;
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const group = groupsById.get(currentId);
        if (!group) break;
        names.unshift(group.name);
        currentId = group.parent_group_id;
      }
      return names.join(' / ') || '未分组文字';
    };
    const grouped = new Map();
    for (const slot of textSlots) {
      const key = slot.group_id || '__ungrouped__';
      if (!grouped.has(key)) {
        grouped.set(key, {
          id: key,
          name: slot.group_id ? getGroupPath(slot.group_id) : '未分组文字',
          slots: [],
        });
      }
      grouped.get(key).slots.push(slot);
    }
    return [...grouped.values()];
  }, [template.slot_groups, textSlots]);
  const [selectedId, setSelectedId] = useState(
    template.default_copy_preset_id || presets[0]?.id || '',
  );
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const selectedPreset = presets.find((preset) => preset.id === selectedId) || null;

  useEffect(() => {
    const nextPreset = presets.find((preset) => preset.id === selectedId)
      || presets.find((preset) => preset.id === template.default_copy_preset_id)
      || presets[0]
      || null;
    if (nextPreset) {
      setSelectedId(nextPreset.id);
      setDraft({ ...nextPreset, values: { ...(nextPreset.values || {}) } });
    } else {
      setSelectedId('');
      setDraft(null);
    }
  }, [presets, selectedId, template.default_copy_preset_id]);

  const run = async (action, fallback) => {
    setBusy(true);
    setMessage('');
    try {
      const result = await action();
      await onChanged();
      return result;
    } catch (error) {
      setMessage(errorMessage(error, fallback));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const createPreset = async ({ name, values }) => {
    const preset = await run(
      () => createJianyingCopyPreset(template.id, {
        name,
        values,
        is_default: presets.length === 0,
        sort_order: presets.length,
      }),
      '新建文案模板失败',
    );
    if (preset) setSelectedId(preset.id);
  };

  const addPreset = () => createPreset({
    name: getAvailableName('新文案', presets),
    values: createEmptyValues(textSlots),
  });

  const duplicatePreset = () => {
    if (!draft) return;
    void createPreset({
      name: getAvailableName(`${draft.name} 副本`, presets),
      values: { ...draft.values },
    });
  };

  const savePreset = () => {
    if (!draft?.name.trim()) return;
    void run(
      () => updateJianyingCopyPreset(template.id, draft.id, {
        name: draft.name.trim(),
        values: Object.fromEntries(textSlots.map((slot) => [
          slot.id,
          String(draft.values?.[slot.id] ?? ''),
        ])),
      }),
      '保存文案模板失败',
    );
  };

  const updatePresetName = (preset, name) => {
    setSelectedId(preset.id);
    setDraft((current) => ({
      ...(current?.id === preset.id ? current : preset),
      name,
      values: { ...(current?.id === preset.id ? current.values : preset.values || {}) },
    }));
  };

  return (
    <div className="jianying-copy-editor">
      <aside className="jianying-copy-editor__list">
        <header>
          <span><strong>文案模板</strong><small>{template.name} 专属</small></span>
          <Button size="sm" variant="outline-primary" disabled={busy || !textSlots.length} onClick={addPreset}>＋ 新建</Button>
        </header>
        <div>
          {presets.map((preset) => (
            <div
              key={preset.id}
              className={`jianying-copy-editor__item ${preset.id === selectedId ? 'is-active' : ''}`}
              onClick={() => setSelectedId(preset.id)}
            >
              <span>
                <Form.Control
                  className="jianying-copy-editor__name"
                  aria-label="文案模板名称"
                  value={preset.id === selectedId ? draft?.name ?? preset.name : preset.name}
                  disabled={busy}
                  onFocus={() => setSelectedId(preset.id)}
                  onChange={(event) => updatePresetName(preset, event.target.value)}
                />
                <small>{Object.keys(preset.values || {}).length} 个文字槽位</small>
              </span>
              {preset.is_default ? <em>默认</em> : null}
            </div>
          ))}
          {!presets.length ? <p>还没有文案模板</p> : null}
        </div>
      </aside>

      <section className="jianying-copy-editor__content">
        {message ? <p className="jianying-notice" role="alert">{message}</p> : null}
        {draft ? (
          <>
            <header>
              <div>
                <h2>{draft.name}</h2>
                <p>名称直接在左侧文案模板列表中修改。</p>
              </div>
              {!selectedPreset?.is_default ? (
                <Button
                  variant="outline-primary"
                  disabled={busy}
                  onClick={() => run(
                    () => updateJianyingCopyPreset(template.id, draft.id, { is_default: true }),
                    '设置默认文案失败',
                  )}
                >
                  设为默认
                </Button>
              ) : <span className="jianying-copy-editor__default">当前默认文案</span>}
            </header>
            <div className="jianying-copy-editor__groups">
              {textSlotGroups.map((group) => (
                <section key={group.id} className="jianying-copy-editor__group">
                  <h3>{group.name}</h3>
                  <div className="jianying-copy-editor__fields">
                    {group.slots.map((slot) => (
                      <CopyValueField
                        key={slot.id}
                        disabled={busy}
                        slot={slot}
                        value={draft.values?.[slot.id] ?? ''}
                        onChange={(value) => setDraft((current) => ({
                          ...current,
                          values: { ...(current.values || {}), [slot.id]: value },
                        }))}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
            <footer>
              <Button
                variant="outline-danger"
                disabled={busy || selectedPreset?.is_default}
                onClick={() => run(
                  () => deleteJianyingCopyPreset(template.id, draft.id),
                  '删除文案模板失败',
                )}
              >
                删除这套文案
              </Button>
              <div>
                <Button variant="outline-secondary" disabled={busy} onClick={duplicatePreset}>复制为新文案</Button>
                <Button disabled={busy || !draft.name?.trim()} onClick={savePreset}>
                  {busy ? <Spinner size="sm" /> : '保存文案'}
                </Button>
              </div>
            </footer>
          </>
        ) : <div className="jianying-empty">先新建一套覆盖全部文字槽位的文案</div>}
      </section>
    </div>
  );
};

export default JianyingCopyPresetEditor;
